import type { ReactNode, InputHTMLAttributes, TextareaHTMLAttributes, SelectHTMLAttributes } from 'react';

type BaseProps = {
  id: string;
  label: string;
  error?: string;
  required?: boolean;
  hint?: string;
};

function labelRow(id: string, label: string, required?: boolean) {
  return (
    <label htmlFor={id} className="block font-heading text-sm font-semibold text-ink-900">
      {label}
      {required ? <span className="ml-0.5 text-accent-600" aria-hidden>*</span> : null}
    </label>
  );
}

const baseInput =
  'mt-1.5 block w-full rounded-xl border bg-white px-3.5 py-2.5 text-base text-ink-900 ' +
  'placeholder:text-ink-500/70 transition-colors duration-150 ' +
  'focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400';

function errId(id: string) {
  return `${id}-error`;
}
function hintId(id: string) {
  return `${id}-hint`;
}

function describedBy(id: string, error?: string, hint?: string) {
  const ids = [hint ? hintId(id) : null, error ? errId(id) : null].filter(Boolean);
  return ids.length ? ids.join(' ') : undefined;
}

function FieldShell({ id, error, hint, children }: { id: string; error?: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      {children}
      {hint && !error ? (
        <p id={hintId(id)} className="mt-1 text-xs text-ink-500">{hint}</p>
      ) : null}
      {error ? (
        <p id={errId(id)} role="alert" className="mt-1 text-sm font-medium text-accent-600">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function TextField({
  id, label, error, required, hint, ...rest
}: BaseProps & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <FieldShell id={id} error={error} hint={hint}>
      {labelRow(id, label, required)}
      <input
        id={id}
        name={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        aria-required={required || undefined}
        className={`${baseInput} ${error ? 'border-accent-600' : 'border-line'}`}
        {...rest}
      />
    </FieldShell>
  );
}

export function TextAreaField({
  id, label, error, required, hint, ...rest
}: BaseProps & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <FieldShell id={id} error={error} hint={hint}>
      {labelRow(id, label, required)}
      <textarea
        id={id}
        name={id}
        rows={3}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        aria-required={required || undefined}
        className={`${baseInput} ${error ? 'border-accent-600' : 'border-line'}`}
        {...rest}
      />
    </FieldShell>
  );
}

export function SelectField({
  id, label, error, required, hint, children, ...rest
}: BaseProps & SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <FieldShell id={id} error={error} hint={hint}>
      {labelRow(id, label, required)}
      <select
        id={id}
        name={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        aria-required={required || undefined}
        className={`${baseInput} cursor-pointer ${error ? 'border-accent-600' : 'border-line'}`}
        {...rest}
      >
        {children}
      </select>
    </FieldShell>
  );
}

export function CheckboxField({
  id, label, error, checked, onChange, hint,
}: {
  id: string;
  label: ReactNode;
  error?: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <FieldShell id={id} error={error} hint={hint}>
      <label htmlFor={id} className="flex cursor-pointer items-start gap-3">
        <input
          id={id}
          name={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, error, hint)}
          className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer rounded border-line text-primary-700 focus:ring-2 focus:ring-primary-400"
        />
        <span className="text-sm leading-relaxed text-ink-700">{label}</span>
      </label>
    </FieldShell>
  );
}
